import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getUserData } from '../utils';
import { authManager } from '../authManager';
import type { UserProfile } from '../types';

// Cache for users data
const usersCache: {
  data: UserProfile[] | null;
  timestamp: number;
  organizationId: string | null;
  expiration: number;
} = {
  data: null,
  timestamp: 0,
  organizationId: null,
  // Cache expiration time: 5 minutes
  expiration: 5 * 60 * 1000,
};

interface SubordinateResult {
  user_id: string;
  level?: number;
}

interface UserStore {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
  fetchUsers: (forceRefresh?: boolean) => Promise<void>;
  fetchDirectSubordinates: (managerId: string) => Promise<string[]>;
  fetchAllSubordinates: (managerId: string) => Promise<string[]>;
}

export const useUserStore = create<UserStore>((set, get) => ({
  users: [],
  loading: false,
  error: null,

  fetchUsers: async (forceRefresh = false) => {
    // PRIMA ottieni l'organizationId per verificare se è cambiata
    let organizationId;
    try {
      const userData = await getUserData();
      organizationId = userData.organizationId;
    } catch (e) {
      // Fallback to direct query if getting user data fails
      console.error('Error getting cached user data, querying directly:', e);
      const user = await authManager.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
      organizationId = userProfile.organization_id;
    }

    if (!organizationId) throw new Error('No organization ID found');

    // Controlla se l'organizzazione è cambiata - se sì, forza il refresh
    const orgChanged = usersCache.organizationId !== null &&
                       usersCache.organizationId !== organizationId;

    if (orgChanged) {
      console.log('[userStore] Organization changed, invalidating cache');
      usersCache.data = null;
      usersCache.timestamp = 0;
      usersCache.organizationId = null;
      set({ users: [] });
    }

    // ORA fai il check per skip - ma non se l'org è cambiata
    if (get().users.length > 0 && !forceRefresh && !orgChanged) {
      return;
    }

    set({ loading: true, error: null });
    try {
      // Use cached data if available and not expired (la cache è già invalidata se orgChanged)
      const now = Date.now();
      if (!forceRefresh && !orgChanged && usersCache.data && now - usersCache.timestamp < usersCache.expiration) {
        set({ users: usersCache.data, loading: false });
        return;
      }

      // Fetch users with their profiles and roles for this organization, excluding superadmin users
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          email,
          full_name,
          phone,
          active,
          avatar_url,
          profile_image_url,
          booking_slug,
          google_account_connected,
          google_workspace_scopes,
          roles:user_roles(
            role:roles(*)
          )
        `)
        .eq('organization_id', organizationId)
        .eq('active', true) // Escludi gli utenti disattivati
        .order('full_name', { ascending: true });

      if (error) throw error;
      
      // Update cache
      usersCache.data = data || [];
      usersCache.timestamp = now;
      usersCache.organizationId = organizationId;
      
      set({ users: data || [] });
    } catch (error) {
      console.error('Error fetching users:', error);
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Fetch subordinati diretti di un manager
   * Usa la funzione RPC get_direct_subordinates
   */
  fetchDirectSubordinates: async (managerId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase
        .rpc('get_direct_subordinates', { p_manager_id: managerId });

      if (error) {
        console.error('Error fetching direct subordinates:', error);
        return [];
      }

      return (data as SubordinateResult[] || []).map(r => r.user_id);
    } catch (error) {
      console.error('Error fetching direct subordinates:', error);
      return [];
    }
  },

  /**
   * Fetch tutti i subordinati (ricorsivo) di un manager
   * Usa la funzione RPC get_all_subordinates
   */
  fetchAllSubordinates: async (managerId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase
        .rpc('get_all_subordinates', { p_manager_id: managerId });

      if (error) {
        console.error('Error fetching all subordinates:', error);
        return [];
      }

      return (data as SubordinateResult[] || []).map(r => r.user_id);
    } catch (error) {
      console.error('Error fetching all subordinates:', error);
      return [];
    }
  }
}));